/**
 * Pendências de estoque (v7): o que foi vendido sem estoque e ainda não saiu do saldo. A
 * regularização faz a baixa real no lote que atendeu a venda (produto com lote; nunca vencido)
 * ou no saldo sem lote, usando só estoque livre de reservas de pedidos aprovados.
 */
import {
  SALES_ORIGIN_LABEL,
  STOCK_SHORTAGE_STATUS_LABEL,
  parseDecimalBR,
  type Page,
  type StockShortageRow,
} from "@cafegestor/shared";
import { useQuery } from "@tanstack/react-query";
import { useState } from "react";
import { useCan } from "@/app/me";
import { Alert, Badge, Button, Card, PageTitle, Select, Spinner, Table } from "@/components/ui";
import { DecimalInput } from "@/components/DecimalInput";
import { apiFetch } from "@/lib/api";
import { Pager, SaveError, SearchInput } from "../cadastros/widgets";
import { dateBR, dateTimeBR, qty, useDocAction } from "./lib";
import { LotSelect, WarehouseSelect } from "./pickers";

const KEY = ["estoque", "pendencias"];
const INVALIDATE = [["estoque"], ["vendas"]];

export function PendenciasPage() {
  const can = useCan();
  const [status, setStatus] = useState<"aberta" | "regularizada" | "cancelada" | "todas">("aberta");
  const [warehouseId, setWarehouseId] = useState("");
  const [q, setQ] = useState("");
  const [offset, setOffset] = useState(0);
  const [open, setOpen] = useState<string | null>(null);
  const params = new URLSearchParams({ status, limit: "50", offset: String(offset) });
  if (warehouseId) params.set("warehouseId", warehouseId);
  if (q.trim()) params.set("q", q.trim());
  const list = useQuery({
    queryKey: [...KEY, params.toString()],
    queryFn: () => apiFetch<Page<StockShortageRow>>(`/estoque/pendencias?${params.toString()}`),
    placeholderData: (p) => p,
  });

  return (
    <div className="space-y-6">
      <PageTitle
        title="Pendências de estoque"
        description="Vendas confirmadas sem estoque suficiente (permitidas pelo perfil de quem vendeu). A falta não saiu do saldo: regularize quando o produto entrar, escolhendo o lote que atendeu a venda ou pelo vencimento mais próximo (FEFO). Enquanto houver pendência, ela conta como compromisso: outras vendas não usam esse estoque."
      />
      <Card>
        <div className="mb-4 flex flex-wrap gap-3">
          <SearchInput
            placeholder="Buscar produto, pedido ou cliente"
            aria-label="Buscar pendências"
            value={q}
            onChange={(v) => {
              setQ(v);
              setOffset(0);
            }}
          />
          <Select
            className="w-56"
            aria-label="Situação"
            value={status}
            onChange={(e) => {
              setStatus(e.target.value as typeof status);
              setOffset(0);
            }}
          >
            <option value="aberta">A regularizar</option>
            <option value="regularizada">Regularizadas</option>
            <option value="cancelada">Canceladas (venda estornada)</option>
            <option value="todas">Todas</option>
          </Select>
          <div className="w-64">
            <WarehouseSelect
              value={warehouseId}
              allOption="Todos os depósitos"
              excludeVendor
              onChange={(v) => {
                setWarehouseId(v);
                setOffset(0);
              }}
            />
          </div>
        </div>
        {list.isPending ? (
          <Spinner />
        ) : (
          <>
            <Table
              head={[
                "Pedido",
                "Cliente",
                "Produto",
                "Depósito",
                "Sem estoque",
                "A regularizar",
                "Autorizada por",
                "Situação",
                "",
              ]}
            >
              {list.data?.items.map((s) => (
                <ShortageRow
                  key={s.id}
                  s={s}
                  open={open === s.id}
                  canRegularize={can("estoque.ajuste.lancar")}
                  onToggle={() => setOpen(open === s.id ? null : s.id)}
                />
              ))}
            </Table>
            {list.data?.items.length === 0 && (
              <p className="mt-3 text-sm text-muted-foreground">Nenhuma pendência encontrada.</p>
            )}
            <Pager total={list.data?.total ?? 0} offset={offset} onOffset={setOffset} />
          </>
        )}
      </Card>
    </div>
  );
}

function ShortageRow({
  s,
  open,
  canRegularize,
  onToggle,
}: {
  s: StockShortageRow;
  open: boolean;
  canRegularize: boolean;
  onToggle: () => void;
}) {
  return (
    <>
      <tr>
        <td className="px-3 py-2 font-mono">
          {s.orderNumber}
          {s.orderOrigin === "venda_direta" && (
            <span className="ml-2 font-sans">
              <Badge>{SALES_ORIGIN_LABEL.venda_direta}</Badge>
            </span>
          )}
          <div className="font-sans text-xs text-muted-foreground">{dateBR(s.orderDate)}</div>
        </td>
        <td className="px-3 py-2">{s.customerName}</td>
        <td className="px-3 py-2">
          <span className="font-mono text-xs text-muted-foreground">{s.productCode}</span>{" "}
          {s.productName}
          {s.controlsLot && <div className="text-xs text-muted-foreground">controla lote</div>}
        </td>
        <td className="px-3 py-2">{s.warehouseName}</td>
        <td className="px-3 py-2 text-right">{qty(s.quantity, s.baseUnitCode)}</td>
        <td className="px-3 py-2 text-right font-medium">{qty(s.open, s.baseUnitCode)}</td>
        <td className="px-3 py-2 text-xs">
          {s.authorizedByEmail ?? "—"}
          <div className="text-muted-foreground">{dateTimeBR(s.authorizedAt)}</div>
        </td>
        <td className="px-3 py-2">
          <Badge
            tone={s.status === "aberta" ? "warn" : s.status === "regularizada" ? "ok" : "neutral"}
          >
            {STOCK_SHORTAGE_STATUS_LABEL[s.status]}
          </Badge>
        </td>
        <td className="px-3 py-2 text-right">
          <Button size="sm" variant="ghost" onClick={onToggle} aria-expanded={open}>
            {open ? "Fechar" : s.status === "aberta" && canRegularize ? "Regularizar" : "Detalhes"}
          </Button>
        </td>
      </tr>
      {open && (
        <tr>
          <td colSpan={9} className="bg-muted/30 px-3 py-3">
            <ShortageDetail s={s} canRegularize={canRegularize} />
          </td>
        </tr>
      )}
    </>
  );
}

function ShortageDetail({ s, canRegularize }: { s: StockShortageRow; canRegularize: boolean }) {
  const [lotId, setLotId] = useState("");
  const [quantity, setQuantity] = useState("");
  const [formError, setFormError] = useState<string | null>(null);
  const regularize = useDocAction<unknown, StockShortageRow>({
    path: () => `/estoque/pendencias/${s.id}/regularizar`,
    invalidate: INVALIDATE,
    successMessage: (r) =>
      r.status === "regularizada"
        ? `Pendência do pedido ${r.orderNumber} regularizada.`
        : `Regularização registrada: faltam ${qty(r.open, r.baseUnitCode)}.`,
  });

  function byLot() {
    const base = parseDecimalBR(quantity, "numero");
    if (base === null || Number(base) <= 0) {
      setFormError("Informe a quantidade, ex.: 1,5.");
      return;
    }
    if (s.controlsLot && !lotId) {
      setFormError("Escolha o lote que atendeu a venda.");
      return;
    }
    setFormError(null);
    regularize.mutate({ mode: "lote", lotId: s.controlsLot ? lotId : null, quantity: base });
  }

  return (
    <div className="space-y-3 text-sm">
      <p className="text-muted-foreground">
        Na venda: vendável {qty(s.sellable, s.baseUnitCode)}; comprometido com outros pedidos e
        pendências {qty(s.committed, s.baseUnitCode)}.
      </p>
      {s.regularizations.length > 0 && (
        <Table head={["Lote", "Quantidade", "Situação", "Por", "Quando"]}>
          {s.regularizations.map((r) => (
            <tr key={r.id}>
              <td className="px-3 py-2 font-mono">{r.lotCode ?? "sem lote"}</td>
              <td className="px-3 py-2 text-right">{qty(r.quantity, s.baseUnitCode)}</td>
              <td className="px-3 py-2">{r.status === "confirmado" ? "Baixada" : "Estornada"}</td>
              <td className="px-3 py-2 text-xs">{r.createdByEmail ?? "—"}</td>
              <td className="px-3 py-2 text-xs">{dateTimeBR(r.createdAt)}</td>
            </tr>
          ))}
        </Table>
      )}
      {s.status === "aberta" && canRegularize && (
        <div className="space-y-2">
          <div className="flex flex-wrap items-end gap-3">
            <Button
              size="sm"
              onClick={() => regularize.mutate({ mode: "fefo" })}
              disabled={regularize.isPending}
            >
              {s.controlsLot
                ? "Regularizar por FEFO (vence primeiro)"
                : "Regularizar o que estiver livre"}
            </Button>
            <span className="text-muted-foreground">ou</span>
            {s.controlsLot && (
              <div className="w-72">
                <LotSelect
                  productId={s.productId}
                  warehouseId={s.warehouseId}
                  value={lotId}
                  onChange={setLotId}
                  emptyLabel="Lote que atendeu a venda"
                />
              </div>
            )}
            <div className="w-32">
              <DecimalInput
                mode="numero"
                value={quantity}
                onChange={(e) => setQuantity(e.target.value)}
                placeholder={`até ${qty(s.open)}`}
                aria-label="Quantidade a regularizar"
              />
            </div>
            <Button size="sm" variant="secondary" onClick={byLot} disabled={regularize.isPending}>
              Regularizar esta quantidade
            </Button>
          </div>
          <p className="text-xs text-muted-foreground">
            Lote vencido nunca atende uma venda. Só o estoque livre de reservas de pedidos aprovados
            é usado; o banco confere tudo de novo.
          </p>
          {formError && <Alert tone="error">{formError}</Alert>}
          <SaveError error={regularize.error} />
        </div>
      )}
    </div>
  );
}
